import React, { useEffect, useState } from "react";
import {
  MessageSquare,
  Eye,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CheckSquare,
  Square,
  Check,
  X,
  Layers2,
  Calculator,
} from "lucide-react";
import {
  caixaGeralService,
  tipoCaixaService,
  usuarioService,
} from "../../services/apiService";
import { useQuery } from "../../hooks/useQuery";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { formatarData } from "../../utils/FormatarData";
import PageContainer from "../../components/PageContainer";
import IntegracaoSisodontoAcao from "./IntegracaoSisodontoAcao";
import IntegracaoSisodontoModal from "./IntegracaoSisodontoModal";
import useIntegracaoSisodonto from "./useIntegracaoSisodonto";
import {
  Button,
  FormField,
  IconButton,
  SelectInput,
  TextInput,
  buttonPageActiveClass,
  buttonPageClass,
  cardClass,
  cardHeaderClass,
  cardHeaderTitleClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellCenterClass,
  tableHeaderCellClass,
  tableWrapperClass,
} from "../../components/ui";

const CaixaGeral = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [tiposCaixa, setTiposCaixa] = useState([]);
  const [caixasSelecionados, setCaixasSelecionados] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [checando, setChecando] = useState(false);
  const [integracaoModalAberto, setIntegracaoModalAberto] = useState(false);
  const [observacaoView, setObservacaoView] = useState("");
  const [showObservacao, setShowObservacao] = useState(false);
  const [filtrosAplicados, setFiltrosAplicados] = useState({
    tipo: "data",
    data: new Date().toLocaleDateString("en-CA"),
    dataInicial: new Date().toLocaleDateString("en-CA"),
    dataFinal: new Date().toLocaleDateString("en-CA"),
    usuarioId: "",
  });
  const [consultaToken, setConsultaToken] = useState(0);

  const [paginacao, setPaginacao] = useState({
    paginaAtual: 1,
    itensPorPagina: 5,
  });
  const [paginacaoData, setPaginacaoData] = useState({
    paginaAtual: 1,
    itensPorPagina: 5,
  });
  const [filtros, setFiltros] = useState({
    tipo: "data",
    data: new Date().toLocaleDateString("en-CA"),
    dataInicial: new Date().toLocaleDateString("en-CA"),
    dataFinal: new Date().toLocaleDateString("en-CA"),
    usuarioId: "",
  });

  const { showError, showSuccess } = useNotification();
  const confirmDialog = useConfirmDialog();

  const { data: usuariosData = [] } = useQuery(
    ["caixa-geral-usuarios"],
    () => usuarioService.listarUsuariosAtivos(),
    {
      onError: (err) => {
        showError(err.message || "Erro ao carregar usuários");
      },
    }
  );

  const { data: tiposCaixaData = [] } = useQuery(
    ["caixa-geral-tipos-caixa"],
    () => tipoCaixaService.listarTipoCaixa(),
    {
      onError: (err) => {
        showError(err.message || "Erro ao carregar tipos de caixa");
      },
    }
  );

  const { data: resumoData, isLoading } = useQuery(
    ["caixa-geral-resumo", consultaToken, filtrosAplicados],
    async () => {
      switch (filtrosAplicados.tipo) {
        case "data":
          return caixaGeralService.listarCaixaGeralPorData(filtrosAplicados.data);
        case "periodo":
          return caixaGeralService.listarCaixaGeralEntreDatas(
            filtrosAplicados.dataInicial,
            filtrosAplicados.dataFinal
          );
        case "usuario":
          if (filtrosAplicados.usuarioId) {
            return caixaGeralService.listarCaixaGeralPorPeriodoEoUsuario(
              filtrosAplicados.usuarioId,
              filtrosAplicados.dataInicial,
              filtrosAplicados.dataFinal
            );
          }
          return { caixa: [], valorTotal: 0 };
        default:
          return caixaGeralService.listarCaixaGeralPorData(filtrosAplicados.data);
      }
    },
    {
      onError: (err) => {
        showError(err.message || "Erro ao carregar caixa geral");
      },
    }
  );

  const resumo = resumoData || { caixa: [], valorTotal: 0 };

  const mostrarObservacao = (observacao) => {
    setObservacaoView(observacao || "Nenhuma observação");
    setShowObservacao(true);
  };

  // Função para ordenar tipos de caixa conforme ordem personalizada
  const normalizarTexto = (texto = "") =>
    texto
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const obterOrdemTipoCaixa = (nome) => {
    const nomeNormalizado = normalizarTexto(nome);
    const tipoCaixaEncontrado = tiposCaixa.find(
      (tipo) => normalizarTexto(tipo.nome) === nomeNormalizado
    );

    return tipoCaixaEncontrado?.ordem ?? Number.MAX_SAFE_INTEGER;
  };

  // Função para ordenar tipos de caixa conforme a ordem cadastrada
  const ordenarTiposCaixa = (tiposCaixaResumo) => {
    return Object.entries(tiposCaixaResumo).sort(([nomeA], [nomeB]) => {
      const ordemA = obterOrdemTipoCaixa(nomeA);
      const ordemB = obterOrdemTipoCaixa(nomeB);

      if (ordemA !== ordemB) return ordemA - ordemB;

      return nomeA.localeCompare(nomeB, "pt-BR", { sensitivity: "base" });
    });
  };

  // Função para ordenar array de movimentos de tipo caixa
  const ordenarMovimentosTipoCaixa = (movimentos) => {
    return [...movimentos].sort((a, b) => {
      const ordemA =
        a.tipoCaixa?.ordem ?? obterOrdemTipoCaixa(a.tipoCaixa?.nome);
      const ordemB =
        b.tipoCaixa?.ordem ?? obterOrdemTipoCaixa(b.tipoCaixa?.nome);

      if (ordemA !== ordemB) return ordemA - ordemB;

      return (a.tipoCaixa?.nome || "").localeCompare(
        b.tipoCaixa?.nome || "",
        "pt-BR",
        { sensitivity: "base" }
      );
    });
  };

  useEffect(() => {
    setUsuarios(usuariosData);
  }, [usuariosData]);

  useEffect(() => {
    setTiposCaixa(tiposCaixaData);
  }, [tiposCaixaData]);

  // Reset página quando aplicar novos filtros
  useEffect(() => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
    setPaginacaoData((prev) => ({ ...prev, paginaAtual: 1 }));
    setCaixasSelecionados([]);
    setSelectionMode(false);
  }, [filtrosAplicados]);

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const aplicarFiltros = () => {
    setFiltrosAplicados({ ...filtros });
    setConsultaToken((token) => token + 1);
  };

  const integracaoSisodonto = useIntegracaoSisodonto({
    onCaixasCriados: (quantidade) => {
      showSuccess(`Integração com o Sisodonto: ${quantidade} caixa(s) criado(s).`);
      aplicarFiltros();
    },
  });

  const fecharModalIntegracao = () => {
    setIntegracaoModalAberto(false);
    integracaoSisodonto.reiniciar();
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setCaixasSelecionados([]);
  };

  // Função para selecionar/deselecionar caixa individual
  const toggleSelecionarCaixa = (caixaId) => {
    setCaixasSelecionados((prev) => {
      if (prev.includes(caixaId)) {
        return prev.filter((id) => id !== caixaId);
      } else {
        return [...prev, caixaId];
      }
    });
  };

  // Função para selecionar/deselecionar todos os caixas visíveis
  const toggleSelecionarTodos = () => {
    const todosCaixasIds = caixasPaginadas.map((caixa) => caixa.id);
    if (caixasSelecionados.length === todosCaixasIds.length) {
      setCaixasSelecionados([]);
    } else {
      setCaixasSelecionados(todosCaixasIds);
    }
  };

  // Função para processar checagem dos caixas selecionados
  const processarChecagem = async () => {
    if (caixasSelecionados.length === 0) {
      showError("Selecione pelo menos um caixa para checagem");
      return;
    }

    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: `Confirma checagem de ${caixasSelecionados.length} caixa(s)?`,
    });

    if (!confirmed) {
      return;
    }

    setChecando(true);
    try {
      const caixasParaChecagem = resumo.caixa.filter((caixa) =>
        caixasSelecionados.includes(caixa.id)
      );

      await caixaGeralService.checarCaixas(caixasParaChecagem);
      showSuccess(
        `${caixasSelecionados.length} caixa(s) marcado(s) como checado(s) com sucesso!`
      );
      setCaixasSelecionados([]);
      setSelectionMode(false);
      setConsultaToken((token) => token + 1);
    } catch (err) {
      showError("Erro ao processar checagem: " + err.message);
    } finally {
      setChecando(false);
    }
  };

  // Função para checar todos os caixas que ainda não foram checados
  const processarChecagemTodos = async () => {
    const caixasNaoChecados = resumo.caixa.filter((caixa) => !caixa.checado);

    if (caixasNaoChecados.length === 0) {
      showError("Não existem caixas pendentes para checar");
      return;
    }

    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: `Confirma checagem de todos os ${caixasNaoChecados.length} caixa(s) pendentes?`,
    });

    if (!confirmed) {
      return;
    }

    setChecando(true);
    try {
      await caixaGeralService.checarTodosCaixas(caixasNaoChecados);
      showSuccess(
        `${caixasNaoChecados.length} caixa(s) marcado(s) como checado(s) com sucesso!`
      );
      setCaixasSelecionados([]);
      setSelectionMode(false);
      setConsultaToken((token) => token + 1);
    } catch (err) {
      showError("Erro ao processar checagem: " + err.message);
    } finally {
      setChecando(false);
    }
  };

  // Cálculos de paginação da tabela principal
  const totalRegistros = resumo.caixa.length;
  const totalPaginas = Math.ceil(totalRegistros / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const caixasPaginadas = resumo.caixa.slice(indiceInicial, indiceFinal);

  // Funções de navegação da tabela principal
  const irParaPrimeiraPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  };

  const irParaPaginaAnterior = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPagina = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
    }));
  };

  const irParaPagina = (numeroPagina) => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  // Funções de navegação para resumo por data
  const irParaPrimeiraPaginaData = () => {
    setPaginacaoData((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPaginaData = (totalPaginasData) => {
    setPaginacaoData((prev) => ({ ...prev, paginaAtual: totalPaginasData }));
  };

  const irParaPaginaAnteriorData = () => {
    setPaginacaoData((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPaginaData = (totalPaginasData) => {
    setPaginacaoData((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginasData, prev.paginaAtual + 1),
    }));
  };

  const irParaPaginaData = (numeroPagina) => {
    setPaginacaoData((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  const caixasNaoChecados = resumo.caixa.filter((caixa) => !caixa.checado);

  const calcularEstatisticas = () => {
    const caixas = resumo.caixa || [];
    const valorTotal = resumo.valorTotal || 0;
    const valorQuebra = caixas.reduce(
      (sum, caixa) => sum + (caixa.valorQuebra || 0),
      0
    );
    const valorRetirada = caixas.reduce(
      (sum, caixa) => sum + (caixa.valorRetirada || 0),
      0
    );
    const valorSangria = caixas.reduce(
      (sum, caixa) => sum + (caixa.valorSangria || 0),
      0
    );

    // Calcular valores por tipo de caixa
    const tipoCaixa = caixas.reduce((acc, caixa) => {
      if (caixa.caixaMovimentoTipoCaixa) {
        caixa.caixaMovimentoTipoCaixa.forEach((movimento) => {
          const nome = movimento.tipoCaixa?.nome || "Não identificado";
          if (!acc[nome]) {
            acc[nome] = 0;
          }
          acc[nome] += movimento.valorMovimento || 0;
        });
      }
      return acc;
    }, {});

    // Calcular total dos tipos de caixa
    const valorTotalTiposCaixa = Object.values(tipoCaixa).reduce(
      (sum, valor) => sum + valor,
      0
    );

    // Calcular valores por forma de pagamento
    const formasPagamento = caixas.reduce((acc, caixa) => {
      if (caixa.caixaMovimento) {
        caixa.caixaMovimento.forEach((movimento) => {
          const nome = movimento.formaPagamento?.nome || "Não identificado";
          if (!acc[nome]) {
            acc[nome] = 0;
          }
          acc[nome] += movimento.valorMovimento || 0;
        });
      }
      return acc;
    }, {});

    // Calcular total das formas de pagamento
    const valorTotalFormasPagamento = Object.values(formasPagamento).reduce(
      (sum, valor) => sum + valor,
      0
    );

    // Agrupar por data com estatísticas completas
    const porData = caixas.reduce((acc, caixa) => {
      try {
        const data = formatarData(caixa.data);
        if (data === "Data inválida") return acc;

        if (!acc[data]) {
          acc[data] = {
            quantidade: 0,
            valor: 0,
            valorRetirada: 0,
            valorQuebra: 0,
            valorQuebraFundo: 0,
            valorSangria: 0,
            tipoCaixa: {},
            formasPagamento: {},
          };
        }
        acc[data].quantidade += 1;
        acc[data].valor += caixa.valorTotal || 0;
        acc[data].valorRetirada += caixa.valorRetirada || 0;
        acc[data].valorSangria += caixa.valorSangria || 0;
        acc[data].valorQuebra += caixa.valorQuebra || 0;
        acc[data].valorQuebraFundo += caixa.valorQuebraFundo || 0;

        // Agregar tipos de caixa por data
        if (caixa.caixaMovimentoTipoCaixa) {
          caixa.caixaMovimentoTipoCaixa.forEach((movimento) => {
            const nome = movimento.tipoCaixa?.nome || "Não identificado";
            if (!acc[data].tipoCaixa[nome]) {
              acc[data].tipoCaixa[nome] = 0;
            }
            acc[data].tipoCaixa[nome] += movimento.valorMovimento || 0;
          });
        }

        // Agregar formas de pagamento por data
        if (caixa.caixaMovimento) {
          caixa.caixaMovimento.forEach((movimento) => {
            const nome = movimento.formaPagamento?.nome || "Não identificado";
            if (!acc[data].formasPagamento[nome]) {
              acc[data].formasPagamento[nome] = 0;
            }
            acc[data].formasPagamento[nome] += movimento.valorMovimento || 0;
          });
        }

        return acc;
      } catch (error) {
        return acc;
      }
    }, {});

    return {
      valorTotal,
      valorQuebra,
      valorRetirada,
      valorSangria,
      tipoCaixa,
      valorTotalTiposCaixa,
      formasPagamento,
      valorTotalFormasPagamento,
      porData,
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  const estatisticas = calcularEstatisticas();

  // Gerar números das páginas para exibição
  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacao.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);

    // Ajustar início se estivermos no final
    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  // Gerar números das páginas para resumo por data
  const gerarNumerosPaginasData = (totalPaginasData) => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacaoData.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginasData, inicio + maxPaginasVisiveis - 1);

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  // Preparar dados paginados para resumo por data
  const dadosPorDataOrdenados = Object.entries(estatisticas.porData).sort(
    ([a], [b]) =>
      new Date(b.split("/").reverse().join("-")).getTime() -
      new Date(a.split("/").reverse().join("-")).getTime()
  );

  const totalRegistrosData = dadosPorDataOrdenados.length;
  const totalPaginasData = Math.ceil(
    totalRegistrosData / paginacaoData.itensPorPagina
  );
  const indiceInicialData =
    (paginacaoData.paginaAtual - 1) * paginacaoData.itensPorPagina;
  const indiceFinalData = indiceInicialData + paginacaoData.itensPorPagina;
  const dadosPorDataPaginados = dadosPorDataOrdenados.slice(
    indiceInicialData,
    indiceFinalData
  );

  return (
    <PageContainer className="py-6 px-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Caixa Geral</h1>
        <p className="text-gray-600">Visão geral dos caixas do sistema</p>
      </div>

      {/* Filtros */}
      <div className={`${cardClass} mb-6 p-4`}>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
          <FormField label="Tipo de Filtro" htmlFor="filtro-tipo">
            <SelectInput
              id="filtro-tipo"
              name="tipo"
              value={filtros.tipo}
              onChange={handleFiltroChange}
            >
              <option value="data">Por Data</option>
              <option value="periodo">Por Período</option>
              <option value="usuario">Por Usuário</option>
            </SelectInput>
          </FormField>

          {filtros.tipo === "data" && (
            <FormField label="Data" htmlFor="filtro-data">
              <TextInput
                id="filtro-data"
                type="date"
                name="data"
                value={filtros.data}
                onChange={handleFiltroChange}
              />
            </FormField>
          )}

          {(filtros.tipo === "periodo" || filtros.tipo === "usuario") && (
            <>
              <FormField label="Data Inicial" htmlFor="filtro-data-inicial">
                <TextInput
                  id="filtro-data-inicial"
                  type="date"
                  name="dataInicial"
                  value={filtros.dataInicial}
                  onChange={handleFiltroChange}
                />
              </FormField>

              <FormField label="Data Final" htmlFor="filtro-data-final">
                <TextInput
                  id="filtro-data-final"
                  type="date"
                  name="dataFinal"
                  value={filtros.dataFinal}
                  onChange={handleFiltroChange}
                />
              </FormField>
            </>
          )}

          {filtros.tipo === "usuario" && (
            <FormField label="Usuário" htmlFor="filtro-usuario">
              <SelectInput
                id="filtro-usuario"
                name="usuarioId"
                value={filtros.usuarioId}
                onChange={handleFiltroChange}
              >
                <option value="">Selecione um usuário</option>
                {usuarios
                  .map(usuario => (
                    <option key={usuario.id} value={usuario.id}>
                      {usuario.nome}
                    </option>
                   
                ))}
              </SelectInput>
            </FormField>
          )}

          <div className="flex gap-2">
            <Button
              onClick={aplicarFiltros}
              disabled={isLoading || checando}
              icon={Filter}
            >
              Filtrar
            </Button>

          </div>
        </div>
      </div>

      <IntegracaoSisodontoAcao
        agente={integracaoSisodonto.agente}
        onAbrir={() => setIntegracaoModalAberto(true)}
      />

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {estatisticas.tipoCaixa &&
          ordenarTiposCaixa(estatisticas.tipoCaixa).map(
            ([nome, valor], index) => (
              <div key={index} className={`${cardClass} p-6`}>
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Layers2 className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      {nome || "N/A"}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(valor || 0)}
                    </p>
                  </div>
                </div>
              </div>
            )
          )}

        <div className={`${cardClass} p-6`}>
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calculator className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">
                Total Tipos Caixa
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatCurrency(estatisticas.valorTotalTiposCaixa)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Resumo por Data */}
      <div className="mb-6">
        <div className={cardClass}>
          <div className={cardHeaderClass}>
              <h3 className={cardHeaderTitleClass}>Resumo por Data</h3>
            <span className="text-sm text-gray-500">
              {totalRegistrosData} registro(s) encontrado(s)
            </span>
          </div>
          <div className="p-4">
            {totalRegistrosData === 0 ? (
              <p className="text-gray-500 text-center py-4">
                Nenhum dado disponível
              </p>
            ) : (
              <>
                <div className="min-h-[100px]">
                  {dadosPorDataPaginados.map(([data, dados]) => (
                    <div key={data} className="">
                      <div className="grid grid-cols-7">
                        <div className="px-3 py-2 text-center">
                          <p className="text-xs text-gray-800">DATA</p>
                          <p className="font-semibold text-sm text-gray-900">
                            {data}
                          </p>
                        </div>
                        <div className="px-3 py-2 text-center">
                          <p className="text-xs text-gray-800">
                            QUANTIDADE DE CAIXA
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {dados.quantidade}
                          </p>
                        </div>
                        <div className="px-3 py-2 text-center">
                          <p className="text-xs text-gray-800">SAÍDAS</p>
                          <p className="text-sm font-medium text-yellow-600">
                            {formatCurrency(dados.valorRetirada)}
                          </p>
                        </div>
                        <div className="px-3 py-2 text-center">
                          <p className="text-xs text-gray-800">SANGRIA</p>
                          <p className="text-sm font-medium text-yellow-600">
                            {formatCurrency(dados.valorSangria)}
                          </p>
                        </div>

                        <div className="px-3 py-2 text-center">
                          <p className="text-xs text-gray-800">QUEBRAS</p>
                          <p
                            className={`text-sm font-medium ${
                              dados.valorQuebra < 0
                                ? "text-red-600"
                                : dados.valorQuebra > 0
                                ? "text-yellow-600"
                                : "text-green-600"
                            }`}
                          >
                            {formatCurrency(dados.valorQuebra)}
                          </p>
                        </div>

                        <div className="px-3 py-2 text-center">
                          <p className="text-xs text-gray-800">
                            QUEBRA DO FUNDO
                          </p>
                          <p
                            className={`text-sm font-medium ${
                              dados.valorQuebraFundo < 0
                                ? "text-red-600"
                                : dados.valorQuebraFundo > 0
                                ? "text-yellow-600"
                                : "text-green-600"
                            }`}
                          >
                            {formatCurrency(dados.valorQuebraFundo)}
                          </p>
                        </div>

                        <div className="row-span-2 px-4 py-2">
                          <p className="text-xs text-gray-800 mb-2">
                            FORMAS DE PAGAMENTO
                          </p>
                          <div className="text-sm text-gray-900">
                            {Object.keys(dados.formasPagamento || {}).length >
                            0 ? (
                              <div className="space-y-1">
                                {Object.entries(dados.formasPagamento).map(
                                  ([nome, valor]) => (
                                    <div
                                      key={nome}
                                      className="flex justify-between text-xs"
                                    >
                                      <span className="truncate max-w-20">
                                        {nome}
                                      </span>
                                      <span className="font-medium">
                                        {formatCurrency(valor)}
                                      </span>
                                    </div>
                                  )
                                )}
                                <div className="border-t pt-1 flex justify-between text-xs font-semibold">
                                  <span>Total:</span>
                                  <span>
                                    {formatCurrency(
                                      Object.values(
                                        dados.formasPagamento
                                      ).reduce((sum, valor) => sum + valor, 0)
                                    )}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">
                                Nenhuma forma
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-span-6 px-4 py-2">
                          <div className="text-sm text-gray-900 ">
                            {Object.keys(dados.tipoCaixa).length > 0 ? (
                              <div className="grid grid-cols-4 gap-2">
                                {ordenarTiposCaixa(dados.tipoCaixa).map(([tipo, valor]) => (
                                  <div
                                    key={tipo}
                                    className="border-2 border-blue-500 rounded px-2 py-2 text-center"
                                  >
                                    <div className="text-600 text-xs truncate">
                                      {tipo.toUpperCase()}
                                    </div>
                                    <div className="text-blue-600 font-semibold text-sm truncate">
                                      {formatCurrency(valor)}
                                    </div>
                                  </div>
                                ))}
                                <div className="border-2 border-blue-500 rounded px-2 py-2 text-center">
                                  <div className="text-600 text-xs truncate">
                                    TOTAL TIPOS CAIXA
                                  </div>
                                  <div className="text-blue-600 font-semibold text-sm truncate">
                                    {formatCurrency(
                                      Object.values(dados.tipoCaixa || {}).reduce(
                                        (sum, valor) => sum + valor,
                                        0
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">
                                Nenhum tipo
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                    {/* Controles de Paginação para Resumo por Data */}
                {totalPaginasData > 1 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        Mostrando {indiceInicialData + 1} até{" "}
                        {Math.min(indiceFinalData, totalRegistrosData)} de{" "}
                        {totalRegistrosData} registros
                      </div>

                      <div className="flex items-center space-x-2">
                        <IconButton
                          onClick={irParaPrimeiraPaginaData}
                          disabled={paginacaoData.paginaAtual === 1}
                          icon={ChevronsLeft}
                          size="sm"
                          label="Primeira página"
                        />

                        <IconButton
                          onClick={irParaPaginaAnteriorData}
                          disabled={paginacaoData.paginaAtual === 1}
                          icon={ChevronLeft}
                          size="sm"
                          label="Página anterior"
                        />

                        <div className="flex space-x-1">
                          {gerarNumerosPaginasData(totalPaginasData).map(
                            (numeroPagina) => (
                              <Button
                                key={numeroPagina}
                                onClick={() => irParaPaginaData(numeroPagina)}
                                variant="ghost"
                                size="sm"
                                className={
                                  paginacaoData.paginaAtual === numeroPagina
                                    ? buttonPageActiveClass
                                    : buttonPageClass
                                }
                              >
                                {numeroPagina}
                              </Button>
                            )
                          )}
                        </div>

                        <IconButton
                          onClick={() =>
                            irParaProximaPaginaData(totalPaginasData)
                          }
                          disabled={
                            paginacaoData.paginaAtual === totalPaginasData
                          }
                          icon={ChevronRight}
                          size="sm"
                          label="Próxima página"
                        />

                        <IconButton
                          onClick={() =>
                            irParaUltimaPaginaData(totalPaginasData)
                          }
                          disabled={
                            paginacaoData.paginaAtual === totalPaginasData
                          }
                          icon={ChevronsRight}
                          size="sm"
                          label="Última página"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

                    {/* Lista Detalhada com Paginação */}
      <div className={cardClass}>
        <div className={cardHeaderClass}>
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold">Lista de Caixas</h3>
            <span className="text-sm text-gray-500">
              {totalRegistros} registro(s) encontrado(s)
            </span>
            {selectionMode && caixasSelecionados.length > 0 && (
              <span className="text-sm text-blue-600 font-medium">
                {caixasSelecionados.length} selecionado(s)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {resumo.caixa.length > 0 && caixasNaoChecados.length > 0 && (
              <Button
                onClick={processarChecagemTodos}
                disabled={isLoading || checando}
                variant="success"
                icon={Check}
              >
                Checar todos ({caixasNaoChecados.length})
              </Button>
            )}

            {selectionMode && caixasSelecionados.length > 0 && (
              <Button
                onClick={processarChecagem}
                disabled={isLoading || checando}
                variant="success"
                icon={Check}
              >
                Checado / Pendente ({caixasSelecionados.length})
              </Button>
            )}

            {resumo.caixa.length > 0 && (
              <Button
                onClick={toggleSelectionMode}
                variant="ghost"
                size="sm"
                className={`${
                  selectionMode
                    ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                }`}
              >
                {selectionMode ? (
                  <>
                    <X size={16} />
                    Cancelar Seleção
                  </>
                ) : (
                  <>
                    <CheckSquare size={16} />
                    Selecionar
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {isLoading || checando ? (
          <div className="p-8 text-center text-gray-500">
            Carregando dados...
          </div>
        ) : resumo.caixa.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhum caixa encontrado
          </div>
        ) : (
          <>
            <div className={tableWrapperClass}>
              <table className={tableClass}>
                <thead className={tableHeadClass}>
                    <tr>
                    {selectionMode && (
                      <th className={tableHeaderCellClass}>
                        <button
                          onClick={toggleSelecionarTodos}
                          className="flex items-center gap-2 hover:text-gray-700"
                          title={
                            caixasSelecionados.length === caixasPaginadas.length
                              ? "Desmarcar todos"
                              : "Selecionar todos"
                          }
                        >
                          {caixasSelecionados.length ===
                          caixasPaginadas.length ? (
                            <CheckSquare size={16} />
                          ) : (
                            <Square size={16} />
                          )}
                          Selecionar
                        </button>
                      </th>
                    )}
                    <th className={tableHeaderCellClass}>
                      Data
                    </th>
                    <th className={tableHeaderCellClass}>
                      Usuário
                    </th>
                    <th className={tableHeaderCellClass}>
                      Status
                    </th>
                    <th className={tableHeaderCellClass}>
                      Checagem
                    </th>
                    <th className={tableHeaderCellClass}>
                      Formas Pagamento
                    </th>
                    <th className={tableHeaderCellClass}>
                      Sangria
                    </th>
                    <th className={tableHeaderCellClass}>
                      Saída
                    </th>
                    <th className={tableHeaderCellClass}>
                      Quebra
                    </th>
                    <th className={tableHeaderCellClass}>
                      Quebra do Fundo
                    </th>
                    <th className={tableHeaderCellCenterClass}>
                      OBS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {caixasPaginadas.map((caixa) => {
                    const movimentosOrdenados = ordenarMovimentosTipoCaixa(
                      caixa.caixaMovimentoTipoCaixa || []
                    );

                    const totalTiposCaixa = movimentosOrdenados.reduce(
                      (sum, mov) => sum + (mov.valorMovimento || 0),
                      0
                    );

                    const formasPagamentoNaoZeradas = (
                      caixa.caixaMovimento || []
                    ).filter((mov) => (mov.valorMovimento || 0) > 0);

                    const totalFormasPagamento =
                      formasPagamentoNaoZeradas.reduce(
                        (sum, mov) => sum + (mov.valorMovimento || 0),
                        0
                      );

                    return (
                      <React.Fragment key={caixa.id}>
                        <tr>
                          {selectionMode && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => toggleSelecionarCaixa(caixa.id)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                {caixasSelecionados.includes(caixa.id) ? (
                                  <CheckSquare
                                    size={16}
                                    className="text-blue-600"
                                  />
                                ) : (
                                  <Square size={16} />
                                )}
                              </button>
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatarData(caixa.data)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {caixa.usuario?.nome ||
                                "Usuário não identificado"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                caixa.aberto
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {caixa.aberto ? "Aberto" : "Fechado"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                caixa.checado
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {caixa.checado ? "Checado" : "Pendente"}
                            </span>
                          </td>
                          <td className="px-6 py-2">
                            <div className="text-sm text-gray-900">
                              {formasPagamentoNaoZeradas.length > 0 ? (
                                <div className="space-y-1">
                                  {formasPagamentoNaoZeradas.map(
                                    (mov, index) => (
                                      <div
                                        key={index}
                                        className="flex justify-between text-xs"
                                      >
                                        <span className="truncate max-w-20">
                                          {mov.formaPagamento?.nome || "N/A"}
                                        </span>
                                        <span className="font-medium">
                                          {formatCurrency(mov.valorMovimento)}
                                        </span>
                                      </div>
                                    )
                                  )}
                                  <div className="border-t pt-1 flex justify-between text-xs font-semibold">
                                    <span>Total:</span>
                                    <span>
                                      {formatCurrency(totalFormasPagamento)}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">
                                  Nenhuma forma
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-2">
                            <div className="text-sm text-yellow-600">
                              {formatCurrency(caixa.valorSangria)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-yellow-600">
                              {formatCurrency(caixa.valorRetirada)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div
                              className={`${
                                caixa.valorQuebra < 0
                                  ? "text-red-900"
                                  : caixa.valorQuebra > 0
                                  ? "text-yellow-900"
                                  : "text-green-900"
                              } font-mediusm text-gray-900`}
                            >
                              {formatCurrency(caixa.valorQuebra)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div
                              className={`${
                                caixa.valorQuebraFundo < 0
                                  ? "text-red-900"
                                  : caixa.valorQuebraFundo > 0
                                  ? "text-yellow-900"
                                  : "text-green-900"
                              } font-mediusm text-gray-900`}
                            >
                              {formatCurrency(caixa.valorQuebraFundo)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {caixa.observacao && caixa.observacao.trim() ? (
                              <button
                                onClick={() =>
                                  mostrarObservacao(caixa.observacao)
                                }
                                className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors flex items-center justify-center gap-1 mx-auto"
                                title="Ver observação"
                              >
                                <Eye size={14} />
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs">
                                Sem observação
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td
                            colSpan={selectionMode ? 11 : 10}
                            className="px-6 py-2 bg-white"
                          >
                            <div className="text-sm text-gray-900">
                              {movimentosOrdenados.length > 0 ? (
                                <div className="grid-cols-4 grid gap-4">
                                  {(() => {
                                    // Converter movimentos para objeto tipoCaixa
                                    const tiposCaixaObj =
                                      movimentosOrdenados.reduce((acc, mov) => {
                                        const nome =
                                          mov.tipoCaixa?.nome || "N/A";
                                        acc[nome] = mov.valorMovimento || 0;
                                        return acc;
                                      }, {});

                                    // Usar a função ordenarTiposCaixa para ordenar
                                    return ordenarTiposCaixa(tiposCaixaObj).map(
                                      ([nome, valor], index) => (
                                        <div
                                          key={index}
                                          className="border-2 border-blue-500 rounded px-3 py-2 text-center flex-1"
                                        >
                                          <div className="text-600 font-medium text-xs">
                                            {nome}
                                          </div>
                                          <div className="text-blue-600 font-semibold text-xs">
                                            {formatCurrency(valor)}
                                          </div>
                                        </div>
                                      )
                                    );
                                  })()}
                                  <div className="border-2 border-blue-500 rounded px-3 py-2 text-center">
                                    <p className="text-600 font-medium text-xs">
                                      Total
                                    </p>
                                    <p className="text-blue-600 font-medium text-xs">
                                      {formatCurrency(totalTiposCaixa)}
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">
                                  Nenhum tipo
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Modal de Observação */}
            {showObservacao && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh]">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <MessageSquare size={20} />
                        Observação
                      </h3>
                      <button
                        onClick={() => setShowObservacao(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X size={20} />
                      </button>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {observacaoView}
                      </p>
                    </div>

                    <div className="flex justify-end mt-4">
                      <button
                        onClick={() => setShowObservacao(false)}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        Fechar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

                    {/* Controles de Paginação */}
            {totalPaginas > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} até{" "}
                    {Math.min(indiceFinal, totalRegistros)} de {totalRegistros}{" "}
                    registros
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Primeira página */}
                    <button
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Primeira página"
                    >
                      <ChevronsLeft size={16} />
                    </button>

                    {/* Página anterior */}
                    <button
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Página anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {/* Números das páginas */}
                    <div className="flex space-x-1">
                      {gerarNumerosPaginas().map((numeroPagina) => (
                        <button
                          key={numeroPagina}
                          onClick={() => irParaPagina(numeroPagina)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            paginacao.paginaAtual === numeroPagina
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {numeroPagina}
                        </button>
                      ))}
                    </div>

                    {/* Próxima página */}
                    <button
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Próxima página"
                    >
                      <ChevronRight size={16} />
                    </button>

                    {/* Última página */}
                    <button
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Última página"
                    >
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Fora do ternario acima (que so renderiza a tabela quando ha
            dados) de proposito: o modal precisa aparecer mesmo sem nenhum
            caixa listado, que e justamente quando a integracao e necessaria. */}
        <IntegracaoSisodontoModal
          open={integracaoModalAberto}
          onClose={fecharModalIntegracao}
          agente={integracaoSisodonto.agente}
          execucao={integracaoSisodonto.execucao}
          dataPadrao={
            filtros.tipo === "data" && filtros.data
              ? filtros.data
              : new Date().toLocaleDateString("en-CA")
          }
          onIniciar={integracaoSisodonto.iniciar}
          onInterromper={integracaoSisodonto.interromper}
          onNovaIntegracao={integracaoSisodonto.reiniciar}
        />
      </div>
    </PageContainer>
  );
};

export default CaixaGeral;





